// contexts/alfaroojfresh/SuppliersContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAppCache } from "../../hooks/Cachecontext";

const SuppliersContext = createContext();

export const useSuppliers = () => {
  const context = useContext(SuppliersContext);
  if (!context) throw new Error("useSuppliers must be used within a SuppliersProvider");
  return context;
};

const BASE = "https://plusback.dims-ai.com";
const TTL  = 5 * 60 * 1000;

const CLASSIFIED_ENDPOINTS = [
  `${BASE}/api/purchase-orders/classify-purchase-orders-api-0`,
  `${BASE}/api/purchase-orders/classify-purchase-orders-api-1`,
  `${BASE}/api/purchase-orders/classify-purchase-orders-api-2`,
];

export const SuppliersProvider = ({ children }) => {
  const { get, set } = useAppCache();

  const [suppliers, setSuppliers]                     = useState([]);
  const [goods, setGoods]                             = useState([]);
  const [classifiedPurchaseOrders, setClassified]     = useState([]);
  const [loading, setLoading]                         = useState(true);
  const [loadingClassified, setLoadingClassified]     = useState(true);
  const [error, setError]                             = useState(null);
  const [errorClassified, setErrorClassified]         = useState(null);

  const fetchSuppliers = useCallback(async () => {
    const key    = "suppliers:list";
    const cached = get(key);
    if (cached) { setSuppliers(cached); return; }

    try {
      const { data } = await axios.get(`${BASE}/api/api-farooj/suppliers`);
      const list = data.flatMap((item) => item.data || []);
      set(key, list, TTL);
      setSuppliers(list);
    } catch (err) {
      console.error("Error fetching suppliers:", err);
      setError("Failed to fetch suppliers.");
      setSuppliers([]);
    }
  }, [get, set]);

  const fetchGoods = useCallback(async () => {
    const key    = "suppliers:goods";
    const cached = get(key);
    if (cached) { setGoods(cached); return; }

    try {
      const { data } = await axios.get(`${BASE}/api/purchase-orders/good-api`);
      const list = data.flatMap((item) => item.data || []);
      set(key, list, TTL);
      setGoods(list);
    } catch (err) {
      console.error("Error fetching goods:", err);
      setError("Failed to fetch goods.");
      setGoods([]);
    }
  }, [get, set]);

  const fetchClassified = useCallback(async () => {
    const key    = "suppliers:classified";
    const cached = get(key);
    if (cached) { setClassified(cached); setLoadingClassified(false); return; }

    try {
      const responses   = await Promise.all(CLASSIFIED_ENDPOINTS.map((url) => axios.get(url)));
      const combined    = responses.flatMap((r) => r.data);

      // Group by supplier id
      const grouped = {};
      combined.forEach(({ supplier }) => {
        if (!supplier?.id) return;
        if (!grouped[supplier.id]) {
          grouped[supplier.id] = { id: supplier.id, name: supplier.name, total: 0, orders: [] };
        }
        grouped[supplier.id].total += supplier.total || 0;
        grouped[supplier.id].orders.push(...(supplier.orders ?? []));
      });

      const list = Object.values(grouped);
      set(key, list, TTL);
      setClassified(list);
    } catch (err) {
      console.error("Error fetching classified POs:", err);
      setErrorClassified("Failed to fetch classified purchase orders.");
      setClassified([]);
    } finally {
      setLoadingClassified(false);
    }
  }, [get, set]);

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      await Promise.all([fetchSuppliers(), fetchGoods(), fetchClassified()]);
      setLoading(false);
    };
    run();
  }, [fetchSuppliers, fetchGoods, fetchClassified]);

  return (
    <SuppliersContext.Provider
      value={{
        suppliers,
        goods,
        classifiedPurchaseOrders,
        loading,
        loadingClassified,
        error,
        errorClassified,
      }}
    >
      {children}
    </SuppliersContext.Provider>
  );
};